import type { CheckedState } from "@radix-ui/react-checkbox";
import { useEffect, useState } from "react";
import type { OnStateChange } from "@/components/mosaic/ui/TileTitle/TileTitle";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";

export type CheckboxPopoverOption = {
	label: string;
	value: string;
};

export type CheckboxPopoverProps = {
	options: CheckboxPopoverOption[];
	checkedOptions: string[];
	stateKey: string;
	buttonText: string;
	displayText: string;
	onStateChange: OnStateChange;
};

export const CheckboxPopover = ({
	options,
	checkedOptions,
	stateKey,
	onStateChange,
	buttonText,
	displayText,
}: CheckboxPopoverProps) => {
	const [open, setOpen] = useState<boolean>(false);
	const [localChecked, setLocalChecked] = useState<string[]>(checkedOptions);

	useEffect(() => {
		setLocalChecked(checkedOptions);
	}, [checkedOptions]);

	const toggleOption = (option: string, checked: CheckedState) => {
		setLocalChecked((prev) =>
			checked ? [...prev, option] : prev.filter((v) => v !== option),
		);
	};

	const handleApplyClick = () => {
		const current = [...localChecked].sort();
		const previous = [...checkedOptions].sort();

		const isEqual =
			current.length === previous.length &&
			current.every((value, index) => value === previous[index]);

		if (!isEqual) {
			onStateChange({ [stateKey]: localChecked }, true);
		}

		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					variant="dropdown"
					className="news-select-trigger text-gold font-bold text-sm h-auto !px-1.5"
				>
					{displayText}
				</Button>
			</PopoverTrigger>
			<PopoverContent className="p-3 w-auto">
				<div className="space-y-2">
					{options.map(({ label, value }) => (
						<div key={value} className="flex gap-2 items-center">
							<Checkbox
								id={`checkbox-${value}`}
								checked={localChecked.includes(value)}
								onCheckedChange={(state) => toggleOption(value, state)}
								aria-label={`Select ${label}`}
							/>
							<label
								htmlFor={`checkbox-${value}`}
								className="cursor-pointer text-light-gray font-normal"
							>
								{label}
							</label>
						</div>
					))}

					<div className="pt-1 flex justify-center">
						<Button
							variant="dropdown"
							className="font-bold px-4 py-1 h-auto"
							onClick={handleApplyClick}
						>
							{buttonText}
						</Button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
};
